/**
 * Constants for the B2B business subdomain that need to be available on BOTH
 * the server and the client. The matching `getHost()` / `isBusinessHost()`
 * helpers stay in `request.server.ts` because they read `request.headers` —
 * a pure server concern.
 *
 * The split is to avoid pulling the `.server.ts` module into the client
 * bundle: meta functions (`meta = ...`) run on the client too, and importing
 * a server-only file from a client-reachable code path leaves a broken
 * module reference that aborts React hydration.
 */
export const BUSINESS_HOST = 'business.archiproamerica.com';

export const BUSINESS_ORIGIN = `https://${BUSINESS_HOST}`;

/**
 * True when a hostname's first DNS label is `business` — the marker for every
 * B2B business subdomain across markets and environments
 * (`business.archiproamerica.com`, `business.archipro.co.nz`,
 * `business.<dev>.team.archiproamerica.com`, ...).
 *
 * This is the strict gate for serving B2B content at the consumer root paths
 * (`/`, `/professionals`, `/brands`). It deliberately does NOT fall back to a
 * "non-production = always true" rule, because the B2B pages now live on the
 * same routes as the consumer site — a permissive check would turn every
 * dev/team/staging consumer homepage into the B2B page.
 */
export const isBusinessHostname = (host: string): boolean =>
    /^business\./i.test(host);

/**
 * Origin of the main consumer site for a given business host: strip the
 * leading `business.` label. Keeps the same environment so dev/team business
 * hosts link back to the matching dev/team consumer apex.
 *
 * `business.archiproamerica.com` -> `https://archiproamerica.com`
 * `business.jason.team.archiproamerica.com` -> `https://jason.team.archiproamerica.com`
 */
export const getMainSiteOrigin = (host: string): string =>
    `https://${host.replace(/^business\./i, '')}`;

/** Canonical origin for the business host itself (e.g. for `<link rel=canonical>`). */
export const getBusinessOrigin = (host: string): string => `https://${host}`;

/**
 * Loader payload returned by the consumer routes (`/`, `/professionals`,
 * `/brands`) when they are reached on a business subdomain and so render a B2B
 * page instead of the consumer page. The `b2b` marker lets the route's
 * component / meta detect the B2B branch at runtime even when the route's
 * `typeof loader` stays typed as the consumer shape.
 */
export interface B2BRootLoaderData {
    b2b: true;
    /** Suppress the global nav/footer/search/chat — see use-hide-navigation. */
    hideChrome: true;
    /** Apex consumer origin for this host, e.g. `https://archiproamerica.com`. */
    mainSiteOrigin: string;
    /** Business origin for canonicals, e.g. `https://business.archiproamerica.com`. */
    businessOrigin: string;
    /**
     * `professionalUrlSegment → profile banner URL` for the partners strip,
     * resolved by the route loader. Optional so the host-only builder stays
     * synchronous; the route merges it in after fetching.
     */
    partnerBanners?: Record<string, string>;
}

/** Build the {@link B2BRootLoaderData} payload from the current request host. */
export const buildB2BRootLoaderData = (host: string): B2BRootLoaderData => ({
    b2b: true,
    hideChrome: true,
    mainSiteOrigin: getMainSiteOrigin(host),
    businessOrigin: getBusinessOrigin(host),
});

/** Runtime guard for {@link B2BRootLoaderData} (the `b2b` marker is truthy). */
export const isB2BRootLoaderData = (data: unknown): data is B2BRootLoaderData =>
    typeof data === 'object' &&
    data !== null &&
    (data as { b2b?: unknown }).b2b === true;
